import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createRoot } from 'react-dom/client';

const PRIMARY = '#e08216';
const FALLBACK_IMAGE = '/img/placeholder-product.png';

const normalizeUrl = (url) => {
  if (!url) return '';
  url = url.trim();
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('/') || url.startsWith('data:')) {
    return url;
  }
  return '/' + url;
};

// Global registry so vanilla-JS in Details.cshtml can control the carousel
let _carouselSetImage = null;
window.setCarouselImage = (url) => { if (_carouselSetImage) _carouselSetImage(url); };

function ProductImageCarousel({ images: initialImages, altText }) {
  const [images] = useState(() => (initialImages || []).map(normalizeUrl).filter(Boolean));
  const [activeIndex, setActiveIndex] = useState(0);
  const [variantImage, setVariantImage] = useState(null);
  const touchStartX = useRef(null);

  // Register the global handler so vanilla JS can switch images
  useEffect(() => {
    _carouselSetImage = (url) => {
      if (!url) return;
      const normalized = normalizeUrl(url);
      const idx = images.indexOf(normalized);
      if (idx !== -1) {
        setVariantImage(null);
        setActiveIndex(idx);
      } else {
        setVariantImage(normalized);
      }
    };
    return () => { _carouselSetImage = null; };
  }, [images]);

  const total = images.length;

  const goNext = useCallback(() => {
    setVariantImage(null);
    setActiveIndex(prev => (prev + 1) % total);
  }, [total]);

  const goPrev = useCallback(() => {
    setVariantImage(null);
    setActiveIndex(prev => (prev - 1 + total) % total);
  }, [total]);

  const goTo = useCallback((idx) => {
    setVariantImage(null);
    setActiveIndex(idx);
  }, []);

  // Touch/swipe for mobile support
  function onTouchStart(e) { touchStartX.current = e.touches[0].clientX; }
  function onTouchEnd(e) {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) { diff > 0 ? goNext() : goPrev(); }
    touchStartX.current = null;
  }

  if (!images || images.length === 0) {
    return (
      <div style={{
        position: 'relative',
        width: '100%',
        aspectRatio: '1 / 1',
        background: '#fff',
        borderRadius: '12px',
        boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <img
          src={FALLBACK_IMAGE}
          alt={altText}
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      </div>
    );
  }

  const activeImage = variantImage || images[activeIndex] || images[0];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

      {/* ── Main image ── */}
      <div
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: '1 / 1',
          background: '#fff',
          borderRadius: '12px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
          overflow: 'hidden',
          userSelect: 'none',
        }}
      >
        {/* Render active image */}
        <img
          src={activeImage}
          alt={altText}
          loading="eager"
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = FALLBACK_IMAGE;
          }}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            transition: 'opacity 0.4s ease, transform 0.4s ease',
          }}
        />

        {/* Left / Right arrows */}
        {total > 1 && (
          <>
            <button
              onClick={goPrev}
              aria-label="Previous image"
              style={{
                position: 'absolute', left: '10px', top: '50%',
                transform: 'translateY(-50%)', zIndex: 10,
                background: 'rgba(255,255,255,0.92)',
                border: `2px solid ${PRIMARY}`,
                borderRadius: '50%', width: '38px', height: '38px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                padding: 0,
              }}
            >
              <svg width="10" height="16" viewBox="0 0 10 16" fill="none">
                <path d="M8 2L2 8L8 14" stroke={PRIMARY} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <button
              onClick={goNext}
              aria-label="Next image"
              style={{
                position: 'absolute', right: '10px', top: '50%',
                transform: 'translateY(-50%)', zIndex: 10,
                background: 'rgba(255,255,255,0.92)',
                border: `2px solid ${PRIMARY}`,
                borderRadius: '50%', width: '38px', height: '38px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                padding: 0,
              }}
            >
              <svg width="10" height="16" viewBox="0 0 10 16" fill="none">
                <path d="M2 2L8 8L2 14" stroke={PRIMARY} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </>
        )}

        {/* Dot indicators */}
        {total > 1 && (
          <div style={{
            position: 'absolute', bottom: '10px', left: '50%',
            transform: 'translateX(-50%)', display: 'flex', gap: '6px', zIndex: 10,
          }}>
            {images.map((_, idx) => (
              <button
                key={idx}
                onClick={() => goTo(idx)}
                aria-label={`Go to image ${idx + 1}`}
                style={{
                  width: (idx === activeIndex && !variantImage) ? '20px' : '8px',
                  height: '8px',
                  borderRadius: '4px',
                  background: (idx === activeIndex && !variantImage) ? PRIMARY : 'rgba(255,255,255,0.7)',
                  border: 'none', cursor: 'pointer', padding: 0,
                  transition: 'width 0.3s ease, background 0.3s ease',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Thumbnails ── */}
      {total > 1 && (
        <div style={{
          display: 'flex', gap: '8px', overflowX: 'auto',
          paddingBottom: '4px', scrollbarWidth: 'thin',
        }}>
          {images.map((src, idx) => (
            <div
              key={idx}
              onClick={() => goTo(idx)}
              style={{
                flexShrink: 0,
                width: '72px', height: '72px',
                borderRadius: '8px', overflow: 'hidden',
                cursor: 'pointer',
                border: `2px solid ${(idx === activeIndex && !variantImage) ? PRIMARY : 'transparent'}`,
                boxShadow: (idx === activeIndex && !variantImage)
                  ? `0 0 0 1px ${PRIMARY}, 0 2px 8px rgba(0,0,0,0.12)`
                  : '0 1px 4px rgba(0,0,0,0.10)',
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                background: '#fff',
              }}
            >
              <img
                src={src}
                alt={`${altText} thumbnail ${idx + 1}`}
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = FALLBACK_IMAGE;
                }}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Mount — runs immediately (script is at bottom of body, DOM is ready) ── */
(function mount() {
  const mountEl = document.getElementById('product-image-carousel');
  if (!mountEl) {
    console.warn('ProductImageCarousel: mount element #product-image-carousel not found');
    return;
  }

  let images = [];
  try {
    const raw = mountEl.getAttribute('data-images');
    images = JSON.parse(raw || '[]');
  } catch (e) {
    console.error('ProductImageCarousel: failed to parse data-images', e);
  }

  const altText = mountEl.getAttribute('data-alt') || 'Product image';

  // Clear any placeholder content
  mountEl.innerHTML = '';
  mountEl.style.minHeight = '';

  const root = createRoot(mountEl);
  root.render(<ProductImageCarousel images={images} altText={altText} />);
})();
